import React, { createContext, useContext, useEffect, useRef, useState } from 'react'

const ProjectContext = createContext(null)

export const useProjectContext = () => {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider')
  }
  return context
}

const deepCopyGrid = (grid) => JSON.parse(JSON.stringify(grid || []))
const gridsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)

export const ProjectProvider = ({ children }) => {
  const [project, setProject] = useState({
    name: 'Untitled Project',
    width: 4,
    height: 4,
    unit: 'in',
    fabricType: 'weavers-cloth',
    stitchesPerInch: 10,
    palette: [],
    grid: [],
  })

  const projectRef = useRef(project)
  useEffect(() => {
    projectRef.current = project
  }, [project])

  const [selectedColor, setSelectedColor] = useState(null)
  const [recentThreads, setRecentThreads] = useState([])
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [hasLoadedProject, setHasLoadedProject] = useState(false)
  const [selectedStitchCell, setSelectedStitchCell] = useState(null)

  const [selectedTool, setSelectedTool] = useState('paint')
  const [brushSize, setBrushSize] = useState(1)
  const [selectedDirection, setSelectedDirection] = useState('none')
  const [selectedLoopHeight, setSelectedLoopHeight] = useState('medium')
  const [showGrid, setShowGrid] = useState(true)
  const [threadType, setThreadType] = useState('stranded')
  const [strandsUsed, setStrandsUsed] = useState(3)
  const [perleSize, setPerleSize] = useState(8)

  const [zoomLevel, setZoomLevel] = useState(1)
  const [previewMode, setPreviewMode] = useState(false)
  const [fullscreenPreview, setFullscreenPreview] = useState(false)
  const [freeStitches, setFreeStitches] = useState([])
  
  const [gridHistory, setGridHistory] = useState({
    past: [],
    present: [],
    future: [],
  })
  

  const isPaintingRef = useRef(false)
  const paintingStartGridRef = useRef(null)
  const paintingWorkingGridRef = useRef(null)

  const HISTORY_LIMIT = 50
  const MIN_ZOOM = 0.5
  const MAX_ZOOM = 4
  const ZOOM_STEP = 0.25

  const syncGridEverywhere = (grid) => {
    const safeGrid = deepCopyGrid(grid)

    projectRef.current = {
      ...projectRef.current,
      grid: safeGrid,
    }

    setProject((prev) => ({
      ...prev,
      grid: safeGrid,
    }))
  }

  const handleSetSelectedColor = (color) => {
    setSelectedColor(color)

    setRecentThreads((prev) => {
      const filtered = prev.filter(
        (item) => (item.code || item.hex) !== (color.code || color.hex)
      )
      return [color, ...filtered].slice(0, 5)
    })
  }

  const addFreeStitch = (stitch) => {
  setFreeStitches((prev) => [
    ...prev,
    {
      id: crypto.randomUUID(),
      ...stitch,
    },
  ])
}

const moveFreeStitch = (id, x, y) => {
  setFreeStitches((prev) =>
    prev.map((s) =>
      s.id === id ? { ...s, x, y } : s
    )
  )
}

const deleteFreeStitch = (id) => {
  setFreeStitches((prev) => prev.filter((s) => s.id !== id))
}

const updateFreeStitch = (id, updates) => {
  setFreeStitches((prev) =>
    prev.map((s) =>
      s.id === id ? { ...s, ...updates } : s
    )
  )
}

  const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z))

  const zoomIn = () => {
    setZoomLevel((z) => clampZoom(Number((z + ZOOM_STEP).toFixed(2))))
  }

  const zoomOut = () => {
    setZoomLevel((z) => clampZoom(Number((z - ZOOM_STEP).toFixed(2))))
  }

  const zoomReset = () => {
    setPanOffset({ x: 0, y: 0 })
    setZoomLevel(1)
  }

  const setZoom = (value) => {
    const v = typeof value === 'number' ? value : parseFloat(value)
    if (Number.isNaN(v)) return

    const normalized = v > 10 ? v / 100 : v
    setZoomLevel(clampZoom(Number(normalized.toFixed(2))))
  }

  const fitGrid = () => {
    const container = document.querySelector('.canvas-container')
    const rulerFrame = document.querySelector('.ruler-frame')

    if (!container || !rulerFrame) return

    const containerRect = container.getBoundingClientRect()
    const frameRect = rulerFrame.getBoundingClientRect()

    if (!frameRect.width || !frameRect.height) return

    const horizontalPadding = 80
    const verticalPadding = 140

    const availableWidth = Math.max(100, containerRect.width - horizontalPadding)
    const availableHeight = Math.max(100, containerRect.height - verticalPadding)

    const fitZoomX = availableWidth / frameRect.width
    const fitZoomY = availableHeight / frameRect.height

    const nextZoom = clampZoom(
      Number(Math.min(fitZoomX, fitZoomY).toFixed(2))
    )

    setPanOffset({ x: 0, y: 0 })
    setZoomLevel(nextZoom)
  }

  const updateProject = (updates) => {
    setProject((prev) => ({ ...prev, ...updates }))
  }

  const addColorToPalette = (color) => {
    setProject((prev) => {
      if (prev.palette.find((c) => c.code === color.code)) {
        return prev
      }

      return {
        ...prev,
        palette: [...prev.palette, color],
      }
    })
  }

  const removeColorFromPalette = (colorCode) => {
    setProject((prev) => ({
      ...prev,
      palette: prev.palette.filter((c) => c.code !== colorCode),
    }))
  }

  const toInches = (value, unit) => (unit === 'cm' ? value / 2.54 : value)

  const initializeGrid = () => {
    setProject((prev) => {
      const unit = prev.unit || 'in'
      const widthIn = toInches(prev.width, unit)
      const heightIn = toInches(prev.height, unit)

      const rows = Math.max(1, Math.ceil(heightIn * prev.stitchesPerInch))
      const cols = Math.max(1, Math.ceil(widthIn * prev.stitchesPerInch))

      const existingGrid = prev.grid || []

      const grid = Array.from({ length: rows }, (_, rowIndex) =>
        Array.from({ length: cols }, (_, colIndex) => {
          const existingCell = existingGrid[rowIndex]?.[colIndex]

          return (
            existingCell || {
              colorId: null,
              direction: 'none',
              loopHeight: 'medium',
            }
          )
        })
      )

      const safeGrid = deepCopyGrid(grid)

      projectRef.current = {
        ...prev,
        grid: safeGrid,
      }

      setGridHistory({
        past: [],
        present: safeGrid,
        future: [],
      })

      return { ...prev, grid: safeGrid }
    })
  }

  const updateCellsBatch = (changes) => {
    if (!changes || changes.length === 0) return

    const historyBaseGrid = isPaintingRef.current
      ? (paintingWorkingGridRef.current || gridHistory.present || projectRef.current?.grid)
      : (gridHistory.present || projectRef.current?.grid)

    if (!historyBaseGrid || historyBaseGrid.length === 0) return

    const newGrid = historyBaseGrid.map((rowArr) =>
      rowArr.map((cell) => ({ ...cell }))
    )

    changes.forEach(({ row, col, updates }) => {
      if (
        row < 0 ||
        col < 0 ||
        row >= newGrid.length ||
        col >= newGrid[0].length
      ) {
        return
      }

      newGrid[row][col] = {
        ...newGrid[row][col],
        ...updates,
      }
    })

    const safeCurrentGrid = historyBaseGrid.map((rowArr) =>
      rowArr.map((cell) => ({ ...cell }))
    )

    const safeNewGrid = newGrid.map((rowArr) =>
      rowArr.map((cell) => ({ ...cell }))
    )

    syncGridEverywhere(safeNewGrid)

    if (isPaintingRef.current) {
      paintingWorkingGridRef.current = safeNewGrid
    }

    setGridHistory((prev) => {
      if (isPaintingRef.current) {
        return {
          ...prev,
          present: safeNewGrid,
        }
      }

      const currentPresent =
        prev.present && prev.present.length > 0
          ? prev.present.map((rowArr) => rowArr.map((cell) => ({ ...cell })))
          : safeCurrentGrid

      return {
        past: [...prev.past, currentPresent].slice(-HISTORY_LIMIT),
        present: safeNewGrid,
        future: [],
      }
    })
  }

  const updateCell = (row, col, updates) => {
    updateCellsBatch([{ row, col, updates }])
  }

  const startPainting = () => {
    if (isPaintingRef.current) return

    const currentGrid = projectRef.current?.grid
    if (!currentGrid || currentGrid.length === 0) return

    isPaintingRef.current = true

    const cloned = deepCopyGrid(currentGrid)
    paintingStartGridRef.current = deepCopyGrid(cloned)
    paintingWorkingGridRef.current = cloned

    syncGridEverywhere(cloned)
  }

  const stopPainting = () => {
    if (!isPaintingRef.current) return

    const startGrid = deepCopyGrid(paintingStartGridRef.current || [])
    const endGrid = deepCopyGrid(
      paintingWorkingGridRef.current || projectRef.current?.grid || []
    )

    if (
      startGrid.length > 0 &&
      endGrid.length > 0 &&
      !gridsEqual(startGrid, endGrid)
    ) {
      setGridHistory((prev) => ({
        past: [...prev.past, startGrid].slice(-HISTORY_LIMIT),
        present: endGrid,
        future: [],
      }))

      syncGridEverywhere(endGrid)
    }

    isPaintingRef.current = false
    paintingStartGridRef.current = null
    paintingWorkingGridRef.current = null
  }

  const undo = () => {
    setGridHistory((prev) => {
      if (prev.past.length === 0) return prev

      const newPast = [...prev.past]
      const previousGrid = newPast.pop()
      const currentGrid = deepCopyGrid(prev.present || [])
      const safePreviousGrid = deepCopyGrid(previousGrid || [])

      syncGridEverywhere(safePreviousGrid)

      if (isPaintingRef.current) {
        paintingWorkingGridRef.current = safePreviousGrid
      }

      return {
        past: newPast,
        present: safePreviousGrid,
        future: [currentGrid, ...prev.future],
      }
    })
  }

  const redo = () => {
    setGridHistory((prev) => {
      if (prev.future.length === 0) return prev

      const nextGrid = deepCopyGrid(prev.future[0] || [])
      const currentGrid = deepCopyGrid(prev.present || [])

      syncGridEverywhere(nextGrid)

      if (isPaintingRef.current) {
        paintingWorkingGridRef.current = nextGrid
      }

      return {
        past: [...prev.past, currentGrid].slice(-HISTORY_LIMIT),
        present: nextGrid,
        future: prev.future.slice(1),
      }
    })
  }

  useEffect(() => {
    const savedProject = localStorage.getItem('punchNeedleProject')

    if (savedProject) {
      try {
        const parsedProject = JSON.parse(savedProject)

        setProject((prev) => ({
          ...prev,
          ...parsedProject,
        }))

        const safeGrid = deepCopyGrid(parsedProject.grid || [])

        projectRef.current = {
          ...projectRef.current,
          ...parsedProject,
          grid: safeGrid,
        }

        setGridHistory({
          past: [],
          present: safeGrid,
          future: [],
        })
      } catch (error) {
        console.error('Failed to load project:', error)
      }
    }

    setHasLoadedProject(true)
  }, [])

  useEffect(() => {
    if (!hasLoadedProject || !project) return

    const timer = setTimeout(() => {
      localStorage.setItem('punchNeedleProject', JSON.stringify(project))
    }, 1000)

    return () => clearTimeout(timer)
  }, [project, hasLoadedProject])

  const value = {
    project,
    updateProject,
    hasLoadedProject,

    selectedStitchCell,
    setSelectedStitchCell,

    selectedColor,
    setSelectedColor: handleSetSelectedColor,
    recentThreads,

    threadType,
    setThreadType,
    strandsUsed,
    setStrandsUsed,
    perleSize,
    setPerleSize,

    selectedTool,
    setSelectedTool,

    brushSize,
    setBrushSize,

    selectedDirection,
    setSelectedDirection,

    selectedLoopHeight,
    setSelectedLoopHeight,

    showGrid,
    setShowGrid,

    zoomLevel,
    setZoomLevel,
    zoomIn,
    zoomOut,
    zoomReset,
    setZoom,
    fitGrid,

    addColorToPalette,
    removeColorFromPalette,

    initializeGrid,
    updateCell,
    updateCellsBatch,

    startPainting,
    stopPainting,

    undo,
    redo,
    canUndo: gridHistory.past.length > 0,
    canRedo: gridHistory.future.length > 0,

    previewMode,
    setPreviewMode,
    fullscreenPreview,
    setFullscreenPreview,

    freeStitches,
    setFreeStitches,
    addFreeStitch,
    moveFreeStitch,
    deleteFreeStitch,
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}